import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import generateExcel from 'zipcelx';
import FreeHoursReport from '@/components/data/hours/FreeHoursReport';
import Icon from '@/components/common/Icon';
import WeekNavigation from '@/components/common/WeekNavigation';
import MondayDate from '@/components/data/date/MondayDate';

const FutureHoursReport = ({ data, weeksToDisplay, onWeeksChange, weeksLabel, weekSelectorClass, formLabelClass, formSelectClass, onStatusChange, onNotesChange }) => {
  const { t } = useTranslation();
  // Add shared filter state that will be applied across all tables
  const [sharedFilters, setSharedFilters] = useState({});

  // Function to update filters across all tables
  const handleFilterChange = (columnId, value) => {
    setSharedFilters(prev => ({
      ...prev,
      [columnId]: value || undefined // Remove empty filters
    }));
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    setSharedFilters({});
  };

  function processData(data) {
    return data.reduce((groups, item) => {
      const date = item.StartDate;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
      return groups;
    }, {});
  }

  const statusLabel = status => (status ? t(`STATUS_INDICATOR.${status.toUpperCase()}`) : t('STATUS_INDICATOR.BLANK'));

  const EXPORT_COLUMNS = [
    { label: 'Date Free', value: item => MondayDate.formatToUIDate(item.StartDate) },
    { label: 'Area', value: item => item.area },
    { label: 'Name', value: item => item.ProfessionalName },
    { label: 'Level', value: item => item.FunctionName },
    { label: 'Office', value: item => item.Office },
    { label: 'Hours', value: item => item.Hours },
    { label: 'Flag', value: item => statusLabel(item.Status) },
    { label: 'Notes', value: item => item.ProfessionalNotes },
    { label: 'Skills', value: item => item.Skills },
    { label: 'Last Engagement', value: item => item.EngagementName }
  ];

  // Mirrors react-table's default 'text' filter (case-insensitive substring
  // match) so the export contains exactly the rows visible on screen.
  const matchesFilters = item =>
    Object.entries(sharedFilters).every(([columnId, filterValue]) => {
      if (filterValue === undefined || filterValue === '') return true;
      return String(item[columnId] ?? '')
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    });

  const handleExportToExcel = () => {
    const visibleData = data.filter(matchesFilters);
    const headerRow = EXPORT_COLUMNS.map(col => ({ value: col.label, type: 'string' }));
    const dataRows = visibleData.map(item =>
      EXPORT_COLUMNS.map(col => {
        const value = col.value(item) ?? '';
        return { value, type: typeof value === 'number' ? 'number' : 'string' };
      })
    );

    generateExcel({
      filename: 'Future_Hours_Report.xlsx',
      sheet: { data: [headerRow, ...dataRows] }
    });
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(sharedFilters).some(value => value !== undefined && value !== '');
  const groupedWeeks = data ? Object.entries(processData(data)) : [];

  return (
    <div>
      <div className='workspace-toolbar availability-toolbar'>
        <div className='workspace-toolbar-group'>
          <button className='workspace-button workspace-button-secondary' onClick={handleExportToExcel} disabled={!data.length}>
            <Icon name='download' size={15} />
            {t('COMMON.EXPORT_TO_EXCEL')}
          </button>
          <button className='workspace-button workspace-button-quiet' onClick={clearAllFilters} disabled={!hasActiveFilters}>
            {t('COMMON.CLEAR_FILTERS')}
          </button>
        </div>
        {onWeeksChange && (
          <div className={weekSelectorClass}>
            <label htmlFor='weeksSelect' className={formLabelClass}>
              {weeksLabel}:
            </label>
            <select id='weeksSelect' value={weeksToDisplay} onChange={onWeeksChange} className={`form-select form-select-sm ${formSelectClass || ''}`}>
              <option value={2}>2 Weeks</option>
              <option value={4}>4 Weeks</option>
              <option value={8}>8 Weeks</option>
              <option value={12}>12 Weeks</option>
            </select>
          </div>
        )}
      </div>

      {hasActiveFilters && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            border: '1px solid #dee2e6'
          }}>
          <span
            style={{
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#495057',
              marginRight: '0.5rem',
              alignSelf: 'center'
            }}>
            Active Filters:
          </span>
          {Object.entries(sharedFilters).map(
            ([key, value]) =>
              value && (
                <div
                  key={key}
                  style={{
                    backgroundColor: '#e9ecef',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.85rem',
                    color: '#495057',
                    fontWeight: '500'
                  }}>
                  <span>
                    {key}: {value}
                  </span>
                  <button
                    aria-label={`Remove ${key} filter`}
                    onClick={() => handleFilterChange(key, '')}
                    style={{
                      marginLeft: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      color: '#6c757d',
                      fontWeight: 'bold',
                      padding: '0',
                      lineHeight: '1'
                    }}>
                    ×
                  </button>
                </div>
              )
          )}
        </div>
      )}

      <WeekNavigation weeks={groupedWeeks.map(([date, report]) => [date, report.filter(matchesFilters)])} />
      {groupedWeeks.map(([date, report]) => (
        <FreeHoursReport key={date} weekId={`week-${date}`} date={MondayDate.formatToUIDate(date)} report={report} sharedFilters={sharedFilters} onFilterChange={handleFilterChange} onStatusChange={onStatusChange} onNotesChange={onNotesChange} />
      ))}
    </div>
  );
};

export default FutureHoursReport;
